import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

interface Point {
  x: number;
  y: number;
}

interface Props {
  data: any[];
  yField: string;
  maxCpcValue?: number | string | null;
  minX?: number;
  maxX?: number;
  threshold?: number
}

const CustomLineChart: React.FC<Props> = ({ data, yField, maxCpcValue, minX, maxX, threshold }) => {

  const { scatter, line } = useMemo(() => {
    const scatter = (data ?? [])
      .filter(p => p.max_cpc != null && p[yField] != null && p.date_day)
      .map(p => ({
        x: Number(p.max_cpc),
        y: Number(p[yField]),
        __ts: new Date(p.date_day).getTime(),
      }));


    const grouped: Record<number, number[]> = {};
    scatter.forEach(({ x, y }) => {
      const key = +x.toFixed(3);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(y);
    });

    const line: Point[] = Object.entries(grouped)
      .map(([x, values]) => ({
        x: Number(x),
        y: values.reduce((a, b) => a + b, 0) / values.length,
      }))
      .sort((a, b) => a.x - b.x);

    return { scatter, line };
  }, [data, yField]);

  const latestPoint =
    scatter
      .filter(p => !isNaN(p.__ts))
      .sort((a, b) => b.__ts - a.__ts)[0] ?? null;

  const latestIndex =
    latestPoint == null
      ? -1
      : scatter.findIndex(p => p.__ts === latestPoint.__ts);


  const roundToStepRange = (min: number, max: number, step = 0.05) => {
    const factor = 1 / step;

    const roundedMin = Math.floor(min * factor) / factor;
    const roundedMax = Math.ceil(max * factor) / factor;

    if (roundedMin === roundedMax) {
      return {
        min: roundedMin - step,
        max: roundedMax + step,
      };
    }

    return {
      min: roundedMin,
      max: roundedMax,
    };
  };

  const { min: safeMinX, max: safeMaxX } = useMemo(() => {
    if (!data?.length) return { min: undefined, max: undefined };

    const values = data
      .map(d => Number(d.max_cpc))
      .filter(v => !isNaN(v));

    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);

    return roundToStepRange(rawMin, rawMax, 0.05);
  }, [data]);

  const options: ApexOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      animations: { enabled: false },
      background: "transparent",
      zoom: { enabled: false },
    },

    grid: {
      show: false,
    },

    legend: { show: false },

    colors: ["#3C454F", "#FFC940"],

    stroke: {
      width: [0, 2.5],
      curve: "smooth",
    },

    markers: {
      size: [5, 0],
      colors: ["rgba(125, 165, 221, .4)"],
      strokeColors: "rgba(125, 165, 221, .4)",
      strokeWidth: 1.2,
      discrete:
        latestIndex >= 0
          ? [
            {
              seriesIndex: 0,
              dataPointIndex: latestIndex,
              fillColor: "#A855F7",
              strokeColor: "#A855F7",
              size: 5,
            },
          ]
          : [],
    },

    xaxis: {
      type: "numeric",
      tooltip: { enabled: false },
      min: safeMinX,
      max: safeMaxX,
      tickAmount: 6,
      labels: {
        formatter: (val) => Number(val).toFixed(2),
        style: {
          colors: "#BFCAD6",
          fontSize: "11px",
        },
      },
      axisBorder: {
        show: true,
        color: "rgba(255,255,255,0.25)",
      },
      axisTicks: { show: false },
      title: {
        text: "Max CPC",
        style: {
          color: "rgba(99, 115, 129, 1)",
          fontSize: "12px",
          fontWeight: "normal",
        },
      },
    },

    yaxis: {
      min: 0,
      max: 100,
      labels: {
        show: true,
        formatter: (val) => Number(val).toFixed(0) + '%',
        style: {
          colors: "#BFCAD6",
          fontSize: "11px",
        },
      },
      axisBorder: {
        show: true,
        color: "rgba(255,255,255,0.25)",
      },
      axisTicks: { show: false },
    },
    annotations:
      maxCpcValue != null || threshold != null
        ? {
          xaxis:
            maxCpcValue != null
              ? [
                {
                  x: Number(maxCpcValue),
                  borderColor: "#00B8D9",
                  strokeDashArray: 0,
                  label: {
                    text: Number(maxCpcValue).toFixed(2),
                    offsetY: -10,
                    borderColor: "transparent",
                    style: {
                      background: "#E5F4FA",
                      color: "rgba(1, 106, 164, 1)",
                      fontSize: "12px",
                      fontWeight: 700,
                      padding: {
                        left: 6,
                        right: 6,
                        top: 2,
                        bottom: 2,
                      },
                    },
                  },
                },
              ]
              : [],

          yaxis:
            typeof threshold === "number"
              ? [
                {
                  y: threshold,
                  borderColor: "rgba(177, 185, 196, 0.90)",
                  strokeDashArray: 4,
                  label: {
                    text: `${threshold}%`,
                    position: "right",
                    offsetX: -15,
                    offsetY: 7,
                    borderColor: "transparent",
                    style: {
                      background: '#E6E8EA',
                      color: "rgba(99, 115, 129, 1)",
                      fontSize: "12px",
                      fontWeight: 400,
                      padding: {
                        left: 4,
                        right: 4,
                        top: 2,
                        bottom: 2,
                      },
                    },
                  },
                },
              ]
              : [],
        }
        : undefined,

    tooltip: {
      enabled: true,
      shared: false,
      intersect: true,
      theme: "dark",

      custom: ({ seriesIndex, dataPointIndex, w }) => {
        if (dataPointIndex < 0) return "";

        // Scatter (raw point)
        if (seriesIndex === 0) {
          const point = w.globals.initialSeries[0].data[dataPointIndex];

          const date = point.__ts
            ? new Date(point.__ts).toLocaleDateString()
            : "—";

          return `
        <div style="width: 160px;">
          <div style="
              display: flex;
              padding: 8px 12px;
              align-items: center;
              gap: 8px;
              z-index: 9999!important;
              align-self: stretch;
              background: #F8F8FA;
              opacity: 0,7;
              color: rgba(99, 115, 129, 1);
              font-size: 14px;
            ">
            ${date}
          </div>
          <div style="
            display: flex;
            padding: 8px 12px;
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
            gap: 2px;
            color: rgba(99, 115, 129, 1);
            align-self: stretch;
            background: rgba(252, 252, 253, 1);
            border-bottom: 1px solid rgba(255, 255, 255, 0.10);
            font-size: 14px;
          ">
          <div>Max CPC</div>
          <div>${point.x.toFixed(3)}</div>
          </div>
          <div style="
          display: flex;
          padding: 8px 12px;
          flex-direction: row;
          justify-content: space-between;
          color: rgba(99, 115, 129, 1);
          align-items: flex-start;
          gap: 2px;
          align-self: stretch;
          background: rgba(252, 252, 253, 1);
          font-size: 14px;
          ">
            <div>Threshold</div>
            <div>${point.y.toFixed(1)}%</div>
          </div>
        </div>
      `;
        }
      },
    },
  };

  const series = [
    { name: "Scatter", type: "scatter" as const, data: scatter },
    { name: "Line", type: "line" as const, data: line },
  ];

  return (
    <div style={{ width: "100%" }}>
      <Chart options={options} series={series} type="line" height={230} />
    </div>
  );
};

export default CustomLineChart;
